import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchNotifications, markNotificationAsRead } from '../store/slices/notificationSlice';
import { branding } from '../constants/branding';
import { FaBell, FaCheck, FaTimes, FaEye, FaEyeSlash, FaClock, FaCheckCircle, FaExclamationTriangle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Notifications.css';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  status: 'UNREAD' | 'READ' | 'DELIVERED';
  createdAt: string;
  senderName?: string;
  isRead: boolean;
  isDelivered: boolean;
}

const Notifications: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications, loading, error } = useAppSelector(state => state.notifications);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 5;

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  // Calculate pagination
  const totalPages = Math.ceil(notifications.length / notificationsPerPage);
  const startIndex = (currentPage - 1) * notificationsPerPage;
  const endIndex = startIndex + notificationsPerPage;
  const currentNotifications = notifications.slice(startIndex, endIndex);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await dispatch(markNotificationAsRead(notificationId));
      // Refresh notifications after marking as read
      dispatch(fetchNotifications());
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'SUCCESS':
        return <FaCheckCircle className="notification-icon success" />;
      case 'WARNING':
        return <FaExclamationTriangle className="notification-icon warning" />;
      case 'ERROR':
        return <FaTimes className="notification-icon error" />;
      default:
        return <FaBell className="notification-icon info" />;
    }
  };

  const getStatusIcon = (status: string, isRead: boolean, isDelivered: boolean) => {
    if (!isDelivered) {
      return <FaClock className="status-icon pending" title="Pending delivery" />;
    }
    if (!isRead) {
      return <FaEyeSlash className="status-icon unread" title="Unread" />;
    }
    return <FaEye className="status-icon read" title="Read" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatRawDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getNotificationTypeClass = (type: string) => {
    switch (type) {
      case 'SUCCESS':
        return 'notification-item success';
      case 'WARNING':
        return 'notification-item warning';
      case 'ERROR':
        return 'notification-item error';
      default:
        return 'notification-item info';
    }
  };

  if (loading) {
    return (
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>Notifications</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>Notifications</h1>
        </div>
        <div className="error-container">
          <p>Error loading notifications: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1>Notifications</h1>
        <div className="notifications-stats">
          <span className="total-count">{notifications.length} total</span>
          <span className="unread-count">
            {notifications.filter(n => !n.isRead).length} unread
          </span>
        </div>
      </div>

      <div className="notifications-content">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <FaBell className="empty-icon" />
            <h3>No notifications</h3>
            <p>You're all caught up! No new notifications at the moment.</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {currentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`${getNotificationTypeClass(notification.type)} ${
                    !notification.isRead ? 'unread' : ''
                  }`}
                  onClick={() => setSelectedNotification(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-header">
                      <div className="notification-icon-container">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-info">
                        <h4 className="notification-title">{notification.title}</h4>
                        <p className="notification-message">{notification.message}</p>
                        <div className="notification-meta">
                          <span className="notification-time">
                            {formatDate(notification.createdAt)}
                          </span>
                          <span className="notification-raw-date">
                            ({formatRawDate(notification.createdAt)})
                          </span>
                          {notification.senderName && (
                            <span className="notification-sender">
                              from {notification.senderName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="notification-actions">
                      {getStatusIcon(notification.status, notification.isRead, notification.isDelivered)}
                      {!notification.isRead && (
                        <button
                          className="mark-read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          title="Mark as read"
                        >
                          <FaCheck />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {startIndex + 1}-{Math.min(endIndex, notifications.length)} of {notifications.length} notifications
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="pagination-page">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    title="Next page"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="modal-overlay" onClick={() => setSelectedNotification(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedNotification.title}</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedNotification(null)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>{selectedNotification.message}</p>
              <div className="modal-meta">
                <span>Type: {selectedNotification.type}</span>
                <span>Status: {selectedNotification.status}</span>
                <span>Created: {formatDate(selectedNotification.createdAt)}</span>
                <span>Raw Date: {formatRawDate(selectedNotification.createdAt)}</span>
                {selectedNotification.senderName && (
                  <span>From: {selectedNotification.senderName}</span>
                )}
              </div>
            </div>
            <div className="modal-actions">
              {!selectedNotification.isRead && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleMarkAsRead(selectedNotification.id);
                    setSelectedNotification(null);
                  }}
                >
                  Mark as Read
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedNotification(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications; 